import Decimal from "decimal.js";
import {
  convertQuoteLineAmount,
  crossRate,
  convertUnitPrice,
  isMultiCurrency,
  formatCurrency as formatCurrencyShared,
  formatUnitPrice as formatUnitPriceShared,
  formatCurrencyWithCode,
  type QuoteCurrencyContext,
  type CurrencyCode,
} from "@forgepoint/shared";

/**
 * The currency-relevant fields read off a quote for display. A subset of
 * QuoteResponse so any quote-shaped object can be passed. `priceDecimals` is
 * the public-quote display hint; internal quotes carry the full
 * `quotePriceRounding` config instead.
 */
export type QuoteCurrencyFields = QuoteCurrencyContext & {
  priceDecimals?: number | null;
};

/**
 * The currency code an amount should be displayed in for a quote.
 * @param quote - The quote's currency fields
 * @returns the customer currency when multi-currency, else the base (or USD)
 */
const displayCurrency = (quote: QuoteCurrencyFields): CurrencyCode =>
  isMultiCurrency(quote) ? quote.quoteCurrency! : (quote.baseCurrency ?? "USD");

/**
 * The configured unit-price display decimals for a quote: the public DTO's
 * `priceDecimals` hint when present, else the internal quote's
 * `quotePriceRounding.decimals`, else undefined (ISO minor-unit default). This
 * is unit-price precision only and must not be applied to payable amounts
 * (line totals, discounts, subtotals, totals), which always render at the
 * display currency's ISO minor units. Only affects how many fraction digits
 * are rendered - the shared unit-price formatter widens to the value's real
 * precision so nothing is hidden.
 * @param quote - The quote's currency fields
 * @returns the display fraction digits, or undefined when rounding is off
 */
const unitPriceDecimals = (quote: QuoteCurrencyFields): number | undefined =>
  quote.priceDecimals ?? quote.quotePriceRounding?.decimals ?? undefined;

const RATE_DECIMALS = 4;

/**
 * The base→quote exchange rate of a multi-currency quote, rounded to 4 dp for
 * display. Null when the quote prices in base currency.
 */
export const quoteCurrencyRate = (
  quote?: QuoteCurrencyFields,
): {
  baseCurrency: CurrencyCode;
  quoteCurrency: CurrencyCode;
  rate: string;
} | null => {
  if (!quote || !isMultiCurrency(quote)) {
    return null;
  }
  const { baseCurrency, quoteCurrency, snapshot } = quote;
  if (!baseCurrency || !quoteCurrency || !snapshot) {
    return null;
  }
  const rate = new Decimal(crossRate(snapshot, baseCurrency, quoteCurrency))
    .toDecimalPlaces(RATE_DECIMALS)
    .toString();
  return { baseCurrency, quoteCurrency, rate };
};

/**
 * Formats a base-currency line/total amount in the quote's display currency,
 * converting and rounding per line and appending the currency code when the
 * quote is multi-currency. Base-currency quotes format as before. Payable
 * amounts always render at the display currency's ISO minor units - the
 * configured price decimals apply to unit prices only.
 * @param baseAmount - decimal string or number in base currency, or null
 * @param quote - The quote's currency fields
 * @returns the formatted amount string, or "-" for null
 */
export const formatQuoteAmount = (
  baseAmount: string | number | null | undefined,
  quote: QuoteCurrencyFields,
): string => {
  if (baseAmount === null || baseAmount === undefined) {
    return "-";
  }
  const currency = displayCurrency(quote);
  if (!isMultiCurrency(quote)) {
    return formatCurrencyShared(baseAmount, currency);
  }
  return formatCurrencyWithCode(
    convertQuoteLineAmount(String(baseAmount), quote),
    currency,
  );
};

/**
 * The backend-computed unit-price fields a hydrated quantity break must expose
 * to render its displayed unit price: `unitPrice` is the derived base-currency
 * value (increment-rounded when the org configures rounding),
 * `unitPriceOverride` the raw manual override in the quote's display currency
 * (never rounded), and `convertedUnitPrice` the presentation-currency unit for
 * multi-currency quotes - the increment-rounded derived unit, or, when
 * overridden, the stored display-currency override echoed verbatim.
 */
interface QuoteUnitPriceFields {
  unitPrice: string | null;
  unitPriceOverride?: string | null;
  convertedUnitPrice?: string | null;
}

/**
 * Formats a quantity break's displayed unit price in the quote's display
 * currency, rendering backend-computed numbers rather than re-deriving them
 * client-side. Selection:
 * - a manual override is denominated in the display currency and formats
 *   verbatim (overrides are never rounded by policy): base-currency quotes
 *   render the raw override; multi-currency quotes render the backend
 *   `convertedUnitPrice`, which echoes the stored override.
 * - otherwise multi-currency renders the backend `convertedUnitPrice` (with a
 *   code suffix); base currency renders the backend `unitPrice`.
 * Unit prices keep their extra precision (no per-line minor-unit rounding).
 * The quote's configured unit-price display decimals only affect how many
 * fraction digits are shown, widening when the value carries more precision.
 * @param qb - The quantity break carrying backend unit-price fields
 * @param quote - The quote's currency fields
 * @returns the formatted unit-price string, or "-" for missing values
 */
export const formatQuoteUnitPrice = (
  qb: QuoteUnitPriceFields | null | undefined,
  quote: QuoteCurrencyFields,
): string => {
  if (!qb) {
    return "-";
  }
  const currency = displayCurrency(quote);
  const decimals = unitPriceDecimals(quote);
  const multiCurrency = isMultiCurrency(quote);

  if (qb.unitPriceOverride !== null && qb.unitPriceOverride !== undefined) {
    if (!multiCurrency) {
      return formatUnitPriceShared(qb.unitPriceOverride, currency, decimals);
    }
    // The backend echoes the stored display-currency override verbatim as
    // `convertedUnitPrice`, so this only selects and formats it - no FX math
    // ever runs client-side for an override.
    if (qb.convertedUnitPrice === null || qb.convertedUnitPrice === undefined) {
      return "-";
    }
    return `${formatUnitPriceShared(qb.convertedUnitPrice, currency, decimals)} ${currency}`;
  }

  if (multiCurrency) {
    if (qb.convertedUnitPrice === null || qb.convertedUnitPrice === undefined) {
      return "-";
    }
    return `${formatUnitPriceShared(qb.convertedUnitPrice, currency, decimals)} ${currency}`;
  }

  if (qb.unitPrice === null) {
    return "-";
  }
  return formatUnitPriceShared(qb.unitPrice, currency, decimals);
};

/**
 * A quantity break's derived (non-override) unit price in the display currency.
 * While the currency context is dirty the backend-stamped `convertedUnitPrice`
 * belongs to the persisted currency, so it is re-derived from the base price.
 */
export const displayedUnitPrice = (
  qb:
    | Pick<QuoteUnitPriceFields, "unitPrice" | "convertedUnitPrice">
    | undefined,
  quote: QuoteCurrencyFields | undefined,
  contextDirty: boolean,
): string | null => {
  if (!quote || !isMultiCurrency(quote)) {
    return qb?.unitPrice ?? null;
  }
  if (contextDirty && qb?.unitPrice) {
    return convertUnitPrice(qb.unitPrice, quote);
  }
  return qb?.convertedUnitPrice ?? null;
};

/**
 * The backend-computed amount fields a hydrated line (quantity break or
 * discount line) must expose to render its total: `amount` is the
 * base-currency value, `convertedAmount` its presentation-currency equivalent
 * for multi-currency quotes.
 */
interface QuoteLineTotalFields {
  amount: string | null;
  convertedAmount?: string | null;
}

/**
 * Formats a hydrated line's total in the display currency, rendering the
 * backend-computed amount (`convertedAmount` for multi-currency quotes, else
 * `amount`) rather than re-deriving it client-side.
 * @param line - The quantity break or discount line carrying backend amounts
 * @param quote - The quote's currency fields
 * @returns the formatted line total, or "-" for missing amounts
 */
export const formatQuoteLineTotal = (
  line: QuoteLineTotalFields | null | undefined,
  quote: QuoteCurrencyFields,
): string => {
  if (!line) {
    return "-";
  }
  const amount = isMultiCurrency(quote) ? line.convertedAmount : line.amount;
  return formatConvertedAmount(amount, quote);
};

/**
 * Formats an amount that has ALREADY been converted into the display currency
 * (e.g. a backend-computed total), appending the code when multi-currency.
 * Payable amounts always render at the display currency's ISO minor units - the
 * configured price decimals apply to unit prices only, never to line totals,
 * discounts, subtotals, or totals.
 * @param convertedAmount - decimal string already in the display currency
 * @param quote - The quote's currency fields
 * @returns the formatted amount string, or "-" for null
 */
export const formatConvertedAmount = (
  convertedAmount: string | number | null | undefined,
  quote: QuoteCurrencyFields,
): string => {
  if (convertedAmount === null || convertedAmount === undefined) {
    return "-";
  }
  const currency = displayCurrency(quote);
  return isMultiCurrency(quote)
    ? formatCurrencyWithCode(convertedAmount, currency)
    : formatCurrencyShared(convertedAmount, currency);
};

/**
 * Display totals for a quote in the display currency, read off the
 * backend-computed totals. The `*Formatted` fields are ready to render.
 */
export interface QuoteDisplayTotals {
  subtotalFormatted: string;
  subtotalWithDiscountsFormatted: string;
  totalFormatted: string;
}

/**
 * The backend-computed, display-currency totals a quote must expose to render
 * its totals section.
 */
interface QuoteTotalsFields {
  subtotal: string | null;
  subtotalWithDiscounts: string | null;
  total: string | null;
}

/**
 * Formats a quote's backend-computed totals for display. The backend already
 * converts totals into the display currency, so this only formats them.
 * @param quote - A quote with backend totals and currency fields
 * @returns the formatted display totals
 */
export const getQuoteDisplayTotals = (
  quote: QuoteCurrencyFields & QuoteTotalsFields,
): QuoteDisplayTotals => ({
  subtotalFormatted: formatConvertedAmount(quote.subtotal, quote),
  subtotalWithDiscountsFormatted: formatConvertedAmount(
    quote.subtotalWithDiscounts,
    quote,
  ),
  totalFormatted: formatConvertedAmount(quote.total, quote),
});
